import * as React from 'react'

/**
 * This hook is used to manually blur & focus first error input (or the one which is focussed now and has error)
 * The reason for this workaround is that we use reakit tooltips to show error message but the tooltip wrapper
 * is not rendered until there's actually an error but the tooltip trigger is rendered, and also it's open state is true
 * if the error input is currently the focused one. Focusing the input sets tooltip open state to true but nothing
 * is shown because there's no error thus the wrapper is not rendered.
 *
 * The issue is that if it's in open state (the input is focussed) and the input becomes invalid (has error)
 * it renderes tooltip wrapper with error message, but it doesn't recalculate (actually it doesn't calculate anything
 * unless both tooltip trigger and tooltip wrapper are rendered) its position, so it seems nothing is shown.
 *
 * This magic workaround triggers changes in effect hook inside reakit, so it recalculates tooltip position, it basically
 * closes and openes the tooltip again. Btw calling manually hide & show after doesn't work the same way.
 *
 * */
export function useFocusInputOnError(errors: unknown) {
  const lastFocusedElement = React.useRef<Element | null>(null)
  React.useEffect(() => {
    let errorInputToFocus = document.querySelector<HTMLInputElement>('.is-error input')
    const activeElement = document.activeElement
    if (lastFocusedElement.current === activeElement) {
      // this is done to prevent focusing another element if user types something
      // into current error input which resets the input error and triggers this effect
      lastFocusedElement.current = null
      return
    }
    if (activeElement?.classList.contains('is-error')) {
      errorInputToFocus = activeElement as HTMLInputElement
    }

    lastFocusedElement.current = errorInputToFocus

    errorInputToFocus?.blur?.()
    setTimeout(() => errorInputToFocus?.focus?.())
  }, [errors])
}
